import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { PageContainer, PageHeader } from '../ui';

export type LockedFeatureVariant = 'page' | 'inline' | 'compact';

export interface LockedFeatureProps {
  /** Cheapest plan that includes the feature, from plan requirements. Null uses the unnamed notice. */
  planName: string | null;
  /** Feature name for the page heading and the accessible name. */
  title?: string;
  variant?: LockedFeatureVariant;
}

/**
 * A plan feature the store can see and cannot run (MOT-794). The notice names
 * the cheapest plan and links to Billing. It does not fetch anything.
 */
export const LockedFeature: React.FC<LockedFeatureProps> = ({ planName, title, variant = 'inline' }) => {
  const { t } = useAppTranslation();
  const notice = planName ? t('planLock.required', { plan: planName }) : t('planLock.requiredUnknown');
  const noticeLabel = title ? `${title}. ${notice}` : notice;

  const body = (
    <Box
      aria-disabled="true"
      aria-label={noticeLabel}
      sx={{
        opacity: 0.72,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover',
        px: variant === 'compact' ? 1.5 : 2,
        py: variant === 'compact' ? 1.25 : 2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <LockOutlined fontSize="small" color="disabled" />
        <Typography variant={variant === 'compact' ? 'body2' : 'body1'} color="text.secondary" sx={{ flex: 1 }}>
          {title && variant !== 'compact' ? `${title}. ` : ''}
          {notice}
        </Typography>
        <Typography
          component={RouterLink}
          to="/app/billing"
          variant="body2"
          color="primary"
          sx={{ flexShrink: 0, fontWeight: 600, textDecoration: 'none' }}
        >
          {t('planLock.seePlans')}
        </Typography>
      </Stack>
    </Box>
  );

  if (variant === 'page') {
    return (
      <PageContainer>
        <PageHeader title={title || t('planLock.pageTitle')} description={t('planLock.pageBody')} />
        {body}
      </PageContainer>
    );
  }
  return body;
};
